import { useLocation, Link } from 'react-router-dom';
import { useAuth, UserRole } from '@/contexts/AuthContext';
import { useSubscription } from '@/hooks/useSubscription';
import { AlertTriangle, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import NotFound from '@/pages/NotFound';
import { getDashboardRouteForRole, isAppUserRole } from '@/lib/access';
import { SafeNavigate } from '@/components/auth/SafeNavigate';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: UserRole[];
  requireSuperAdmin?: boolean;
}

function AccessStateCard({
  title,
  description,
  actionLabel,
  actionHref,
}: {
  title: string;
  description: string;
  actionLabel?: string;
  actionHref?: string;
}) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-6">
      <div className="max-w-lg rounded-3xl border border-border bg-card p-8 text-center shadow-soft">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10">
          <AlertTriangle className="h-7 w-7 text-primary" />
        </div>
        <h1 className="mt-5 text-2xl font-bold text-foreground">{title}</h1>
        <p className="mt-3 text-sm leading-6 text-muted-foreground">{description}</p>
        {actionLabel && actionHref ? (
          <Button asChild className="mt-6">
            <Link to={actionHref}>{actionLabel}</Link>
          </Button>
        ) : null}
      </div>
    </div>
  );
}

export function ProtectedRoute({ children, allowedRoles, requireSuperAdmin = false }: ProtectedRouteProps) {
  const { user, profile, isLoading, isSuperAdmin } = useAuth();
  const { isRestricted, isLoading: subscriptionLoading } = useSubscription();
  const location = useLocation();

  const protectedPaths = [
    '/lawyer-dashboard',
    '/deal-intake',
    '/matters/',
    '/agent-dashboard',
    '/deal-status/',
    '/client-dashboard',
    '/client-onboarding',
    '/client-transaction/',
    '/transactions',
    '/properties',
    '/messages',
    '/documents',
    '/calendar',
    '/notifications',
    '/payments',
    '/checkout',
    '/legal-review',
    '/due-diligence',
    '/settings',
    '/help',
    '/superadmin-dashboard',
  ];

  const isKnownProtectedPath = protectedPaths.some((path) =>
    location.pathname === path || location.pathname.startsWith(path),
  );

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 rounded-xl gradient-primary flex items-center justify-center animate-pulse">
            <Shield className="w-6 h-6 text-primary-foreground" />
          </div>
          <p className="text-muted-foreground text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    if (!isKnownProtectedPath) {
      return <NotFound />;
    }
    return <SafeNavigate to="/login" replace />;
  }

  if (!profile) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 rounded-xl gradient-primary flex items-center justify-center animate-pulse">
            <Shield className="w-6 h-6 text-primary-foreground" />
          </div>
          <p className="text-muted-foreground text-sm">Setting up your profile...</p>
        </div>
      </div>
    );
  }

  const hasValidRole = isAppUserRole(profile.role);
  const incompleteProfileRoutes = ['/client-onboarding', '/settings', '/help'];
  const isIncompleteProfileRoute = incompleteProfileRoutes.some((path) =>
    location.pathname === path || location.pathname.startsWith(path),
  );

  if (!isSuperAdmin && !hasValidRole && !isIncompleteProfileRoute) {
    return <SafeNavigate to="/client-onboarding?setup=role" replace />;
  }

  const isAdminRoute = location.pathname.startsWith('/superadmin-dashboard');
  const adminAllowedRoutes = ['/superadmin-dashboard', '/settings', '/help'];

  if (isSuperAdmin) {
    if (!adminAllowedRoutes.some((path) => location.pathname === path || location.pathname.startsWith(path))) {
      return <SafeNavigate to="/superadmin-dashboard" replace />;
    }
  } else if (isAdminRoute || requireSuperAdmin) {
    return <SafeNavigate to={getDashboardRouteForRole(profile.role)} replace />;
  }

  if (requireSuperAdmin && !isSuperAdmin) {
    return <SafeNavigate to={getDashboardRouteForRole(profile.role)} replace />;
  }

  if (allowedRoles && hasValidRole && !allowedRoles.includes(profile.role as UserRole)) {
    return <SafeNavigate to={isSuperAdmin ? '/superadmin-dashboard' : getDashboardRouteForRole(profile.role)} replace />;
  }

  if (!isSuperAdmin) {
    if (profile.account_status === 'blocked') {
      return (
        <AccessStateCard
          title="Your account is blocked"
          description={profile.account_status_reason || 'PropTRUST support needs to review your access before you can continue using the platform.'}
          actionLabel="Contact Support"
          actionHref="/help"
        />
      );
    }

    if (profile.account_status === 'suspended') {
      return (
        <AccessStateCard
          title="Your account is suspended"
          description={profile.account_status_reason || 'Your PropTRUST access is temporarily paused while an administrator reviews your account.'}
          actionLabel="View Help"
          actionHref="/help"
        />
      );
    }
  }

  if (subscriptionLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 rounded-xl gradient-primary flex items-center justify-center animate-pulse">
            <Shield className="w-6 h-6 text-primary-foreground" />
          </div>
          <p className="text-muted-foreground text-sm">Checking your workspace access...</p>
        </div>
      </div>
    );
  }

  const isCheckoutRoute = location.pathname === '/checkout';
  if (!isSuperAdmin && isRestricted && !isCheckoutRoute) {
    return <SafeNavigate to={`/checkout?redirect=${encodeURIComponent(location.pathname)}`} replace />;
  }

  return <>{children}</>;
}
